//Color select component
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { updateUser as updateUserAction } from '../../store/slices/authSlice';
import { updateUser } from '../../services/firestore';
import { COLOR_PALETTE, ColorKey, DEFAULT_COLOR_KEY } from '../../utils/colors';


interface ColorPickerProps {
  className?: string;
  onColorChange?: (color: ColorKey) => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ 
  className = '',
  onColorChange 
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [selectedColor, setSelectedColor] = useState<ColorKey>(
    (user?.preferredColor as ColorKey) || DEFAULT_COLOR_KEY //Default
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  //Set default if no color
  useEffect(() => {
    if (user && !user.preferredColor) {
      console.log('Setting default color for user:', user.username);
      handleColorSelect(DEFAULT_COLOR_KEY);
    }
  }, [user]);

  const handleColorSelect = async (colorKey: ColorKey) => {
    if (!user) return;

    setSelectedColor(colorKey);
    setIsUpdating(true);

    try {
      //Update state
      dispatch(updateUserAction({ preferredColor: colorKey }));
      
      //Callback
      onColorChange?.(colorKey);
      
      //DB update
      await updateUser(user.uid, { 
        preferredColor: colorKey,
        updatedAt: new Date()
      });
      
      console.log(`Color updated to ${colorKey} for user ${user.username}`);
      
    } catch (error) {
      console.error('Failed to update color:', error);
      //Revert on error
      dispatch(updateUserAction({ preferredColor: user.preferredColor }));
      setSelectedColor((user.preferredColor as ColorKey) || 'blue');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!user) return null;

  return (
    <div className={`color-picker ${className}`}>
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Choose Your Color
        </h3>
        <p className="text-sm text-gray-600">
          This color will appear on your trees, avatar highlights, and ghost dot when other players see you.
        </p>
      </div>

      {/* Current Color Preview */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-4">
          <div className="text-sm font-medium text-gray-700">Current color:</div>
          <div className="flex items-center space-x-2">
            <div 
              className="w-6 h-6 rounded-full border-2 border-white shadow-md"
              style={{ backgroundColor: COLOR_PALETTE[selectedColor].value }}
            />
            <span className="text-sm font-medium text-gray-800">
              {COLOR_PALETTE[selectedColor].name}
            </span>
            <span className="text-xs text-gray-500">
              • {COLOR_PALETTE[selectedColor].description}
            </span>
          </div>
        </div>
      </div>

      {/* Color Grid */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {Object.entries(COLOR_PALETTE).map(([key, color]) => (
          <motion.button
            key={key}
            className={`relative p-3 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${
              selectedColor === key 
                ? 'border-gray-800 shadow-lg' 
                : 'border-gray-200 hover:border-gray-400'
            }`}
            style={{ backgroundColor: `${color.value}15` }} //15% opacity
            onClick={() => handleColorSelect(key as ColorKey)}
            disabled={isUpdating}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            {/* Color Circle */}
            <div 
              className="w-8 h-8 rounded-full mx-auto mb-2 border-2 border-white shadow-md"
              style={{ backgroundColor: color.value }}
            />
            
            {/* Color Name */}
            <div className="text-xs font-medium text-gray-700 text-center">
              {color.name}
            </div>

            {/* Selected Indicator */}
            {selectedColor === key && (
              <motion.div 
                className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </motion.div>
            )}
          </motion.button>
        ))}
      </div>

      {/* Preview Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Preview your color:</span>
          <button
            className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>

        {showPreview && (
          <motion.div 
            className="p-4 bg-green-100 rounded-lg border border-green-200"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="space-y-3">
              {/* Ghost Dot Preview */}
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600 w-20">Ghost dot:</span>
                <div className="relative">
                  <div 
                    className="w-5 h-5 rounded-full blur-sm animate-pulse absolute"
                    style={{ backgroundColor: `${COLOR_PALETTE[selectedColor].value}40` }}
                  />
                  <div 
                    className="relative w-4 h-4 rounded-full border-2 shadow-md flex items-center justify-center"
                    style={{
                      backgroundColor: COLOR_PALETTE[selectedColor].value,
                      borderColor: `${COLOR_PALETTE[selectedColor].value}80`,
                    }}
                  >
                    <span className="text-white text-xs font-bold">
                      {user.initials}
                    </span>
                  </div>
                </div>
                <span className="text-xs text-gray-500">How you appear to other players</span>
              </div>

              {/* Tree Preview */}
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600 w-20">Tree tint:</span>
                <div className="relative">
                  <div className="w-6 h-6 bg-green-600 rounded-sm"></div>
                  <div 
                    className="absolute inset-0 rounded-sm opacity-30"
                    style={{ backgroundColor: COLOR_PALETTE[selectedColor].value }}
                  />
                </div>
                <span className="text-xs text-gray-500">Subtle overlay on your planted trees</span>
              </div>

              {/* Avatar Highlight Preview */}
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600 w-20">Avatar:</span>
                <div className="relative">
                  <div className="w-6 h-6 bg-orange-400 rounded-full"></div>
                  <div 
                    className="absolute inset-0 rounded-full opacity-20 border-2"
                    style={{ 
                      backgroundColor: COLOR_PALETTE[selectedColor].value,
                      borderColor: COLOR_PALETTE[selectedColor].value 
                    }}
                  />
                </div>
                <span className="text-xs text-gray-500">Colored border around your avatar</span>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Status */}
      {isUpdating && (
        <div className="mt-4 flex items-center space-x-2 text-sm text-gray-600">
          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Updating color...</span>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
export { COLOR_PALETTE, type ColorKey };